import { Button } from '@/components/ui/design-system';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from '@/hooks/use-translation';
import { Icon, Icons } from '../icon';
import { P3 } from '../ui/text';

interface DefaultEmptyStateProps {
  /** Whether filters are currently active */
  hasActiveFilters?: boolean;
  /** Callback to clear all filters */
  onClearFilters?: () => void;
}

/**
 * Default empty state component for the table
 */
export function DefaultEmptyState({ hasActiveFilters, onClearFilters }: DefaultEmptyStateProps) {
  const { t } = useTranslation('desktop_ui');

  return (
    <div className="flex flex-col items-center justify-center py-16 text-secondary">
      <Icon name={Icons.MagnifyingGlass} className="text-l" />
      <p className="text-body-14">
        {hasActiveFilters
          ? t('components.table.emptyState.noMatchingRecords')
          : t('components.table.emptyState.noData')}
      </p>
      <p className="mt-1 text-tertiary text-body-12">
        {hasActiveFilters
          ? t('components.table.emptyState.noMatchingRecordsDescription')
          : t('components.table.emptyState.noDataDescription')}
      </p>
      {hasActiveFilters && onClearFilters && (
        <Button variant="outline" size="sm" className="mt-4" onClick={onClearFilters}>
          <P3>{t('components.table.emptyState.clearFilters')}</P3>
        </Button>
      )}
    </div>
  );
}

interface LoadingColumn {
  id: string;
  size: number;
}

interface DefaultLoadingStateProps {
  columns?: LoadingColumn[];
  headerHeight?: number;
  rowHeight?: number;
  rowCount?: number;
}

/**
 * Default loading state component for the table
 * Renders skeleton rows matching the table's visual style
 */
export function DefaultLoadingState({
  columns,
  headerHeight = 48,
  rowHeight = 40,
  rowCount = 10,
}: DefaultLoadingStateProps) {
  // Fallback to default columns if none provided
  const loadingColumns = columns ?? [
    { id: '0', size: 176 },
    { id: '1', size: 176 },
    { id: '2', size: 176 },
    { id: '3', size: 176 },
  ];

  return (
    <div className="flex flex-col">
      {/* Skeleton header row */}
      <div className="flex w-full" style={{ height: headerHeight }}>
        {loadingColumns.map((col) => (
          <div
            key={`loading-header-${col.id}`}
            className="flex shrink-0 items-center border-b border-r border-primary-5 bg-white px-3 py-3.5"
            style={{ width: col.size }}
          >
            <Skeleton className="h-4 w-1/2 bg-primary-10" />
          </div>
        ))}
      </div>
      {/* Skeleton body rows */}
      {Array.from({ length: rowCount }, (_, i) => `row-${i}`).map((rowId) => (
        <div
          key={rowId}
          className="flex w-full border-b border-b-primary-5"
          style={{ height: rowHeight }}
        >
          {loadingColumns.map((col) => (
            <div
              key={`${rowId}-cell-${col.id}`}
              className="flex shrink-0 items-center border-r border-r-primary-5 bg-white px-3"
              style={{ width: col.size }}
            >
              <Skeleton className="h-4 w-3/4 bg-primary-10" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
